import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import useFetch from "../../../hooks/useFetch";
import SpinerLoading from "../../../components/ui/loader/SpinerLoading";
import AlertError from "../../../components/ui/error/AlertError";
import { totalAmount } from "../../../utils/amount";

const {
  REACT_APP_MOSJID_LOGO: mosjidLogo,
  REACT_APP_MOSJID_DETAILS_NAME: mosjidDetailsName,
  REACT_APP_MOSJID_ADDRESS: mosjidAddress,
} = process.env;

const PaymentView = ({ isOpen, closeModal, paymentId }) => {
  const { loading, data, error } = useFetch(`/payment/get/${paymentId}`);

  let content;
  if (loading) content = <SpinerLoading />;
  else if (error) content = <AlertError message={error} />;
  else if (data) {
    const {
      email,
      contactNumber,
      referenceId,
      status,
      receipt,
      cardType,
      items,
    } = data;
    content = (
      <>
        <div className="flex justify-between">
          <div>
            <img
              src={mosjidLogo}
              alt={mosjidDetailsName}
              className="w-10 h-10"
            />
            <h4 className="text-xl font-normal mt-3">Donation System</h4>
          </div>
          <div className="text-sm font-normal text-right">
            <p>{mosjidAddress}</p>
            <p>New Zealand</p>
          </div>
        </div>
        <div className="mt-12">
          <h4 className="text-xl font-normal mb-3">Customer Information</h4>
          <div className="py-3 border-t-2 border-b-2 border-gray-400 text-[12px]">
            <p>
              <span className="font-medium mr-8">Email-Address: </span>
              {email}
            </p>
            <p>
              <span className="font-medium mr-5">Contact Number: </span>
              {contactNumber}
            </p>
          </div>
        </div>
        <div className="mt-12">
          <h4 className="text-xl font-normal mb-3">Other Information</h4>
          <div className="py-3 border-t-2 border-b-2 border-gray-400 text-[12px] grid grid-cols-2">
            <div className="col-span-1">
              <p>
                <span className="font-medium mr-8">Ref ID: </span>
                {referenceId}
              </p>
              <p>
                <span className="font-medium mr-7">Status: </span>
                {status}
              </p>
            </div>
            <div className="col-span-1">
              <p>
                <span className="font-medium mr-8">E-Receipt: </span>
                {receipt}
              </p>
              <p>
                <span className="font-medium mr-7">Card Type: </span>
                {cardType}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-12">
          <h4 className="text-xl font-normal mb-3">Donation List</h4>
          <div className="text-[12px]">
            <table className="w-full">
              <thead className="text-[12px] border-t-2 border-gray-400">
                <tr>
                  <th scope="col" className="py-3 font-medium">
                    Name
                  </th>
                  <th scope="col" className="py-3 font-medium">
                    Quantity
                  </th>
                  <th scope="col" className="py-3 text-center font-medium">
                    Price
                  </th>
                  <th scope="col" className="py-3 text-right font-medium">
                    Sub Total
                  </th>
                </tr>
              </thead>
              <tbody className="text-[12px]">
                {items.map((item) => (
                  <tr key={item._id} className="border-t-2 border-gray-400">
                    <th scope="col" className="py-3 font-normal">
                      {item.name}
                    </th>
                    <th scope="col" className="py-3 font-normal">
                      {item.qty}
                    </th>
                    <th scope="col" className="py-3 text-center font-normal">
                      $ {item.qty ? item.price / item.qty : item.price}
                    </th>
                    <th scope="col" className="py-3 text-right font-normal">
                      $ {item.price}
                    </th>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-[12px] border-t-2 border-gray-400">
                <tr>
                  <th scope="col" className="py-3 font-medium"></th>
                  <th scope="col" className="py-3 font-medium"></th>
                  <th scope="col" className="py-3 text-center font-medium">
                    Total
                  </th>
                  <th scope="col" className="py-3 text-right font-medium">
                    $ {totalAmount(items)}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {content}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PaymentView;
